import React, { FC, useEffect, useMemo, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import boddVariables from '../../../../variables.module.scss';
import MeasureUtils, { Measure } from '../../measure-utils';

const calculateWidth = (text: string) => {
    if (text.length * 10 > 20) {
        return (text.length + 1) * 10;
    }
    return 20;
};

interface MetricInputComponentProps {
    disabled?: boolean | undefined,
    value?: number | null | undefined,
    onBlur?: ((millimeters: number | null) => void) | undefined,
    measure?: Measure | undefined,
    prefix?: number | undefined,
    defaultValue?: number | null,
}

const MetricInputComponent: FC<MetricInputComponentProps> = (props) => {
    const { disabled, value, onBlur, measure, prefix, defaultValue } = props;

    const decimalPlaces = useMemo(() => MeasureUtils.getDecimalPlaces(measure ?? Measure.mm), [measure]);
    const measurementScale = useMemo(() => MeasureUtils.getMeasurementScale(measure ?? Measure.mm), [measure]);

    const displayPrefix = `${((prefix ?? 0) * measurementScale).toFixed(decimalPlaces)} -`;

    const [textValue, setTextValue] = useState(value != null ? (value * measurementScale).toFixed(decimalPlaces) : '');
    const [inputWidth, setInputWidth] = useState(calculateWidth(((value ?? 0) * measurementScale).toFixed(decimalPlaces)));

    useEffect(() => {
        const text = value != null ? (value * measurementScale).toFixed(decimalPlaces) : '';
        setTextValue(text);
        setInputWidth(calculateWidth(text));
    }, [value, measure]);

    const handleOnBlur: (event: React.ChangeEvent<HTMLInputElement>) => void = (event) => {
        if (onBlur) {
            const currentValue = parseFloat(event.target.value);
            if (Number.isNaN(currentValue)) {
                onBlur(null);
            } else {
                onBlur(currentValue / measurementScale);
            }
        }
    };

    const handleOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void = (event) => {
        setTextValue(event.target.value ?? defaultValue?.toString() ?? '');
        setInputWidth(calculateWidth(event.target.value));
    };

    return (
        <NumericFormat
            data-testid="NumericTextFieldComponent"
            decimalScale={decimalPlaces}
            disabled={disabled}
            fixedDecimalScale
            customInput={TextField}
            slotProps={prefix !== undefined ? ({
                input: {
                    startAdornment:
                        <InputAdornment
                            position="end"
                            sx={{ '.MuiTypography-root': { color: boddVariables.grey200 }, pointerEvents: 'none' }}
                        >
                            {displayPrefix}
                        </InputAdornment>,
                },
            }) : {}}
            sx={{
                '& input': {
                    textAlign: 'right',
                    width: `${inputWidth}px`,
                    height: '1.5em',
                    lineHeight: '1.5em',
                },
                border: 'none',
                '& fieldset': { border: 'none' },
            }}
            name="value"
            onBlur={(e) => handleOnBlur(e)}
            value={textValue}
            onChange={handleOnChange}
            onFocus={(e) => e.target.select()}
        />
    );
};

export default MetricInputComponent;

MetricInputComponent.defaultProps = {
    disabled: false,
    onBlur: () => null,
    value: undefined,
    measure: undefined,
    prefix: undefined,
    defaultValue: 0,
};
